import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaReceipt, FaTag, FaFileInvoice, FaBuilding, FaUser, FaFlag, FaExclamationTriangle } from "react-icons/fa";
import {
  getPaymentRequests, getPaymentRequestInbox,
  getPaymentRequestFilterOptions, getPaymentRequestTabCounts,
} from "redux/erp/paymentRequestSlice";
import { Button, EmptyState, LoadingSpinner } from "components/common";
import PageShell from "./common/PageShell";
import FilterDropdown from "./common/FilterDropdown";
import DateRangeFilter from "./common/DateRangeFilter";
import Clickable from "./common/Clickable";
import TableToolbar, { ToolbarActionsMenu } from "./common/TableToolbar";
import Pagination from "./common/Pagination";
import IndeterminateCheckbox from "./common/IndeterminateCheckbox";
import BulkActionBar from "./common/BulkActionBar";
import ListHeaderCell from "./common/ListHeaderCell";
import useResizableColumns from "./common/useResizableColumns";
import BulkApproveForm from "./detail/BulkApproveForm";
import usePaymentRequestQuery, { TABS } from "./common/usePaymentRequestQuery";
import PaymentRequestSettingsModal from "./PaymentRequestSettingsModal";
import InvoiceFilesModal from "./InvoiceFilesModal";
import {
  FORM_TYPES, FORM_TYPE_OPTIONS, STATUS_OPTIONS, PRIORITY_OPTIONS, DEFAULT_WIDTHS, columnsForTab,
} from "./listColumns";

// Trần 1 lô ký hàng loạt — phải khớp BULK_APPROVE_LIMIT ở BaseController.
const BULK_APPROVE_LIMIT = 50;

// Độ rộng cột người dùng kéo — nhớ theo trình duyệt, dùng chung mọi tab.
const COLUMN_WIDTHS_KEY = "erp.payment-requests.column-widths.v1";
// Cột cố định (checkbox / file / chevron) — không kéo giãn.
const CHECK_COL_WIDTH = 40;
const FILES_COL_WIDTH = 64;
const CHEVRON_COL_WIDTH = 32;

// Thứ tự tab. Tab "For accountant" cũ đã bỏ (2026-10-24): nhóm kế toán thấy phiếu
// ở bước Accountant trong "Waiting for approval", nhóm Payment Accountant thấy phiếu
// phải chi trong "Unpaid"; phần còn lại vốn đã nằm trong All/Approved.
// Ai cũng thấy đủ các tab — nội dung mỗi tab đã được BE giới hạn theo
// quyền đọc của chính user (Erp::PaymentRequest::Visibility), nên một nhân viên
// thường mở "All" chỉ thấy phiếu mình lập/đã ký/đang chờ mình ký.
const TAB_DEFS = [
  { k: TABS.ALL, label: "All", icon: "bi-collection" },
  { k: TABS.MINE, label: "My requests", icon: "bi-person-lines-fill" },
  { k: TABS.INBOX, label: "Waiting for approval", icon: "bi-pen", attention: true },
  { k: TABS.APPROVED, label: "Approved", icon: "bi-check2-circle" },
  { k: TABS.REJECTED, label: "Rejected", icon: "bi-x-circle" },
  { k: TABS.UNPAID, label: "Unpaid", icon: "bi-bank", attention: true,
    title: "Approved, money not paid yet — HSBC not paid, or non-HSBC still with the Payment Accountant" },
];
// Header ô bảng — Geist: chữ nhỏ, xám, không giãn nặng/không đen.
// Sticky phải đặt trên chính <th>, KHÔNG đặt trên <thead>: với border-collapse
// collapse thì Chrome không sticky được thead/tr, nên ô góc (checkbox chọn tất cả)
// không dính khi cuộn ngang. Bảng dùng border-separate + border tự vẽ trên từng ô.
// Tách padding khỏi TH_BASE: nếu để "tw-px-4" trong base rồi ô góc nối thêm
// "tw-px-3", Tailwind vẫn ăn px-4 (thắng theo thứ tự trong stylesheet, không phải
// thứ tự viết trong className) — ghi đè kiểu đó là bẫy im lặng.
// overflow-hidden không đặt ở đây: tay nắm kéo giãn nằm sát mép phải của ô.
const TH_BASE = "tw-sticky tw-top-0 tw-z-20 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-whitespace-nowrap tw-bg-geist-bg-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border";
const TH = `${TH_BASE} tw-px-4`;
const TH_CHECK = `${TH_BASE} tw-px-3 tw-left-0 tw-z-30`;
// Cột ghim trái: nền đặc (không để chữ cột khác trượt xuyên qua) + bóng mép phải
// báo hiệu còn cột bên dưới khi cuộn ngang. left đặt qua style (phụ thuộc tab).
const STICKY_EDGE = "tw-shadow-[1px_0_0_0_#eaeaea,4px_0_8px_-4px_rgba(0,0,0,0.08)]";
const STICKY_TH = `tw-z-30 ${STICKY_EDGE}`;
const STICKY_TD = `tw-sticky tw-z-10 ${STICKY_EDGE}`;

const PaymentRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    allData, inboxData, filterOptions, tabCounts,
    listLoading, inboxLoading,
    listError, inboxError,
  } = useSelector((s) => s.erp_payment_request);
  const isAdmin = useSelector((s) => s.profile_user?.data?.is_admin);

  const {
    query, setQuery, resetFilters, apiParams, activeFilterCount, setDateRange, dateParams,
  } = usePaymentRequestQuery();
  const { tab } = query;

  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Phiếu đang mở xem file hoá đơn (null = đóng modal).
  const [filesFor, setFilesFor] = useState(null);

  // Ô search gõ tới đâu hiện tới đó, nhưng chỉ đẩy lên URL/API sau 300ms —
  // nếu ghi thẳng vào URL mỗi ký tự thì history bị rác và gọi API liên tục.
  const [searchDraft, setSearchDraft] = useState(query.q);
  const searchTimer = useRef(null);
  const lastPushedSearch = useRef(query.q);

  useEffect(() => {
    // Query đổi từ bên ngoài (Back/Forward, Clear filters) -> đồng bộ lại ô search.
    if (query.q !== lastPushedSearch.current) {
      lastPushedSearch.current = query.q;
      setSearchDraft(query.q);
    }
  }, [query.q]);

  const onSearchChange = useCallback((value) => {
    setSearchDraft(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      lastPushedSearch.current = value;
      setQuery({ q: value }, { replace: true });
    }, 300);
  }, [setQuery]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const isInbox = tab === TABS.INBOX;
  const listState = isInbox ? inboxData : allData;
  const loading = isInbox ? inboxLoading : listLoading;
  const error = isInbox ? inboxError : listError;

  const fetchList = useCallback(() => {
    if (isInbox) dispatch(getPaymentRequestInbox(apiParams));
    else dispatch(getPaymentRequests(apiParams));
    // Badge đi cùng mỗi lần tải lại list — ký xong / đổi tab là số khớp ngay.
    dispatch(getPaymentRequestTabCounts(dateParams));
  }, [dispatch, isInbox, apiParams, dateParams]);

  useEffect(fetchList, [fetchList]);
  useEffect(() => { dispatch(getPaymentRequestFilterOptions()); }, [dispatch]);

  // Lọc/sắp xếp/phân trang đều ở server — rows dùng thẳng, không filter lại ở client.
  const rows = useMemo(
    () => (listState?.rows || []).map((r) => r.attributes || r),
    [listState]
  );

  // Bỏ chọn khi đổi tab/filter/trang: người ký không bao giờ được ký thứ mình
  // không còn nhìn thấy trên màn hình.
  const selectionKey = `${tab}|${JSON.stringify(apiParams)}`;
  const lastSelectionKey = useRef(selectionKey);
  useEffect(() => {
    if (lastSelectionKey.current !== selectionKey) {
      lastSelectionKey.current = selectionKey;
      setSelectedIds([]);
    }
  }, [selectionKey]);

  const isApprovalTab = isInbox;
  const columns = useMemo(() => columnsForTab(tab), [tab]);
  const { widthOf, startResize, nudge, resetWidth } = useResizableColumns(COLUMN_WIDTHS_KEY, DEFAULT_WIDTHS);
  const resize = useMemo(() => ({ start: startResize, nudge, reset: resetWidth }), [startResize, nudge, resetWidth]);
  // Bảng rộng = tổng các cột; cột chevron (không khai độ rộng) ăn phần thừa khi
  // màn hình rộng hơn, còn hẹp hơn thì khung cuộn ngang.
  const tableMinWidth = columns.reduce((sum, c) => sum + widthOf(c.key), CHEVRON_COL_WIDTH)
    + (isApprovalTab ? CHECK_COL_WIDTH + FILES_COL_WIDTH : 0);
  // Cột ghim (Request) nằm ngay sau cột checkbox ở tab chờ ký, sát mép trái ở tab khác.
  const stickyLeft = isApprovalTab ? CHECK_COL_WIDTH : 0;
  const onSort = useCallback(({ sort, dir }) => setQuery({ sort, dir }), [setQuery]);
  const selectableIds = useMemo(
    () => (isApprovalTab ? rows.filter((r) => r.permissions?.allow_approve).map((r) => r.id) : []),
    [isApprovalTab, rows]
  );
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedSet.has(id));

  const toggleRow = (id) => setSelectedIds((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));
  const toggleAll = (checked) => setSelectedIds(checked ? selectableIds : []);

  const requestorOptions = useMemo(
    () => (filterOptions?.requestors || []).map((r) => ({ value: r.value, label: r.label })),
    [filterOptions]
  );
  const departmentOptions = useMemo(
    () => (filterOptions?.departments || []).map((d) => ({ value: d, label: d })),
    [filterOptions]
  );

  const openNew = (type) => { setNewMenuOpen(false); navigate(`/payment-requests/new?type=${type}`); };


  const filters = (
    <>
      <DateRangeFilter value={query.date} onChange={setDateRange} />
      <FilterDropdown label="Status" icon={FaTag} value={query.status} options={STATUS_OPTIONS}
        onChange={(v) => setQuery({ status: v })} />
      <FilterDropdown label="Form type" icon={FaFileInvoice} value={query.form_type} options={FORM_TYPE_OPTIONS}
        onChange={(v) => setQuery({ form_type: v })} />
      <FilterDropdown label="Priority" icon={FaFlag} value={query.priority} options={PRIORITY_OPTIONS}
        onChange={(v) => setQuery({ priority: v })} />
      <FilterDropdown label="Department" icon={FaBuilding} value={query.department} options={departmentOptions}
        onChange={(v) => setQuery({ department: v })} />
      <FilterDropdown label="Requestor" icon={FaUser} value={query.requestor} options={requestorOptions}
        onChange={(v) => setQuery({ requestor: v })} />
    </>
  );

  const header = (
    <TableToolbar
      title="Payment Requests"
      subtitle="Payment Request Form — QF-FNC"
      search={searchDraft}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search code, payee, dept..."
      activeFilterCount={activeFilterCount}
      onClearFilters={resetFilters}
      filters={filters}
      actionsMenu={(
        <ToolbarActionsMenu items={[
          {
            key: "settings",
            label: isAdmin ? "Payment settings" : "My signature settings",
            icon: "bi-gear",
            onClick: () => setSettingsOpen(true),
          },
        ]} />
      )}
      actions={(
        <div className="tw-relative">
          <Clickable onClick={() => setNewMenuOpen((o) => !o)}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2 tw-transition-colors">
            <i className="bi bi-plus-lg tw-text-sm" /> New request
            <i className={`bi ${newMenuOpen ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[10px] tw-opacity-80`} />
          </Clickable>
          {newMenuOpen && (
            <>
              <div className="tw-fixed tw-inset-0 tw-z-40" onClick={() => setNewMenuOpen(false)} />
              <div className="tw-absolute tw-right-0 tw-mt-1 tw-w-64 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-z-50 tw-overflow-hidden tw-py-1">
                {FORM_TYPES.map((ft) => (
                  <Clickable key={ft.value} onClick={() => openNew(ft.value)}
                    className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 hover:tw-bg-geist-100 tw-transition-colors">
                    <i className={`bi ${ft.icon} tw-text-base tw-text-geist-fg-2 tw-w-4 tw-text-center tw-flex-shrink-0`} />
                    <span className="tw-flex-1 tw-min-w-0">
                      <span className="tw-block tw-text-[12px] tw-font-semibold tw-text-geist-fg tw-truncate">{ft.label}</span>
                      <span className="tw-block tw-text-[10px] tw-text-geist-fg-3 tw-truncate">{ft.desc}</span>
                    </span>
                    <span className="tw-text-[9px] tw-font-semibold tw-font-geist-mono tw-text-geist-fg-3 tw-flex-shrink-0">{ft.iso}</span>
                  </Clickable>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      segmented={(
        <div role="tablist" aria-label="Payment request lists"
          className="tw-inline-flex tw-max-w-full tw-overflow-x-auto tw-p-0.5 tw-bg-geist-100 tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm">
          {TAB_DEFS.map((m) => {
            const active = tab === m.k;
            const count = tabCounts?.[m.k];
            // Tab "việc phải làm" (chờ ký / chưa chi) tô badge khi còn phiếu.
            const urgent = m.attention && count > 0;
            return (
              <Clickable key={m.k} role="tab" aria-selected={active} title={m.title}
                onClick={() => setQuery({ tab: m.k === TABS.MINE ? "" : m.k })}
                className={`tw-inline-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-whitespace-nowrap tw-rounded-[4px] tw-transition ${
                  active ? "tw-bg-geist-bg tw-text-geist-fg tw-shadow-geist-card" : "tw-text-geist-fg-3 hover:tw-text-geist-fg"
                }`}>
                <i className={`bi ${m.icon} tw-text-[11px]`} /> {m.label}
                {count != null && (
                  <span className={`tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-text-[10px] tw-font-semibold tw-tabular-nums ${
                    urgent ? "tw-bg-geist-accent tw-text-white" : "tw-bg-geist-200 tw-text-geist-fg-2"
                  }`}>
                    {count > 999 ? "999+" : count}
                  </span>
                )}
              </Clickable>
            );
          })}
        </div>
      )}
      trailing={(
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-text-xs tw-text-geist-fg-3">
            {(listState?.total_rows ?? rows.length).toLocaleString("en-US")} item(s)
          </span>
        </div>
      )}
    />
  );

  const hasQuery = Boolean(query.q) || activeFilterCount > 0;

  return (
    <>
      <PageShell header={header}>
        {loading ? (
          <LoadingSpinner message="Loading..." />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-gap-3 tw-px-6 tw-text-center">
            <FaExclamationTriangle className="tw-text-geist-error" size={24} />
            <div>
              <p className="tw-text-[15px] tw-font-semibold tw-text-geist-fg tw-m-0">Could not load payment requests</p>
              <p className="tw-text-[13px] tw-text-geist-fg-2 tw-m-0 tw-mt-1 tw-max-w-md">{error}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={fetchList}>Retry</Button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={FaReceipt}
            title={hasQuery ? "No matching requests" : "No payment requests"}
            description={hasQuery ? "Try changing the search or filters." : ""} />
        ) : (
          /* h-full + flex-col: card bảng ăn hết chiều cao còn lại của trang,
             thân bảng tự cuộn, thanh phân trang dính đáy card. */
          <div className="tw-p-4 sm:tw-p-6 tw-h-full tw-flex tw-flex-col tw-min-h-0">
            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
              {/* table-scroll: thanh cuộn mảnh, ẩn khi nghỉ — chỉ hiện khi rê chuột / focus vào bảng. */}
              <div className="table-scroll tw-flex-1 tw-min-h-0 tw-overflow-auto">
                <table className="tw-w-full tw-border-separate tw-table-fixed" style={{ borderSpacing: 0, minWidth: tableMinWidth }}>
                  <colgroup>
                    {isApprovalTab && <col style={{ width: CHECK_COL_WIDTH }} />}
                    {columns.map((c) => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                    {isApprovalTab && <col style={{ width: FILES_COL_WIDTH }} />}
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      {isApprovalTab && (
                        <th className={TH_CHECK}>
                          <IndeterminateCheckbox
                            checked={allSelected}
                            indeterminate={selectedIds.length > 0 && !allSelected}
                            disabled={selectableIds.length === 0}
                            onChange={toggleAll}
                            title="Select all on this page"
                          />
                        </th>
                      )}
                      {columns.map((c) => (
                        <ListHeaderCell key={c.key} column={c} style={c.sticky ? { left: stickyLeft } : undefined}
                          className={`${TH} ${c.align === "right" ? "tw-text-right" : ""} ${c.sticky ? STICKY_TH : ""}`}
                          sort={query.sort} dir={query.dir} onSort={onSort} resize={resize} />
                      ))}
                      {isApprovalTab && <th className={`${TH} tw-text-center`}>Files</th>}
                      <th className={TH} aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a, rowIdx) => {
                      const canApprove = Boolean(a.permissions?.allow_approve);
                      const checked = selectedSet.has(a.id);
                      // Dòng cuối không kẻ đáy: thanh phân trang đã có border-top,
                      // để cả hai sẽ thành 2 vạch sát nhau.
                      const cellBorder = rowIdx === rows.length - 1
                        ? ""
                        : "tw-border-0 tw-border-b tw-border-solid tw-border-geist-border";
                      return (
                        <tr key={a.id}
                          className={`tw-group tw-transition-colors ${
                            checked ? "tw-bg-geist-accent/5" : "hover:tw-bg-geist-bg-2"
                          }`}>
                          {isApprovalTab && (
                            <td className={`${cellBorder} tw-px-3 tw-py-3 tw-align-middle tw-sticky tw-left-0 tw-z-10 ${checked ? "tw-bg-[#f0f7ff]" : "tw-bg-geist-bg group-hover:tw-bg-geist-bg-2"}`}
                              onClick={(e) => e.stopPropagation()}>
                              <IndeterminateCheckbox
                                checked={checked}
                                disabled={!canApprove}
                                onChange={() => toggleRow(a.id)}
                                title={canApprove ? "Select for bulk approval" : "Not awaiting your signature"}
                              />
                            </td>
                          )}

                          {/* overflow-hidden + min-w-0: cột kéo hẹp thì nội dung cắt "…" chứ không tràn sang ô bên. */}
                          {columns.map((c) => (
                            <td key={c.key} style={c.sticky ? { left: stickyLeft } : undefined}
                              className={`${cellBorder} tw-px-4 tw-py-3 tw-align-middle tw-overflow-hidden ${
                                c.sticky ? `${STICKY_TD} ${checked ? "tw-bg-[#f0f7ff]" : "tw-bg-geist-bg group-hover:tw-bg-geist-bg-2"}` : ""
                              }`}>
                              {c.render(a)}
                            </td>
                          ))}

                          {/* Xem file hoá đơn ngay tại danh sách — không phải rời trang
                              (rời đi là mất hết checkbox đang tick để ký hàng loạt) */}
                          {isApprovalTab && (() => {
                            const fileCount = (a.invoice_files || []).length;
                            return (
                              <td className={`${cellBorder} tw-px-2 tw-py-3 tw-align-middle tw-text-center`}
                                onClick={(e) => e.stopPropagation()}>
                                <Clickable
                                  onClick={() => setFilesFor(a)}
                                  disabled={fileCount === 0}
                                  title={fileCount === 0 ? "No invoice file linked" : `View ${fileCount} invoice file(s)`}
                                  className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-geist-sm tw-border tw-border-solid tw-text-[11px] tw-font-semibold tw-transition-colors ${
                                    fileCount === 0
                                      ? "tw-border-geist-border tw-text-geist-fg-3"
                                      : "tw-border-geist-border tw-text-geist-fg-2 hover:tw-border-geist-accent hover:tw-text-geist-accent"
                                  }`}
                                >
                                  <i className="bi bi-file-earmark-pdf" />
                                  {fileCount > 0 && fileCount}
                                </Clickable>
                              </td>
                            );
                          })()}

                          {/* Cột đệm ăn phần rộng thừa (không còn chevron: dòng không click được nữa). */}
                          <td className={cellBorder} />
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="tw-flex-shrink-0">
                <Pagination
                  pagination={listState?.pagination}
                  perPage={query.per_page}
                  onPageChange={(page) => setQuery({ page })}
                  onPerPageChange={(per_page) => setQuery({ per_page })}
                />
              </div>
            </div>

            {isApprovalTab && (
              <BulkActionBar
                count={selectedIds.length}
                limit={BULK_APPROVE_LIMIT}
                onApprove={() => setBulkOpen(true)}
                onClear={() => setSelectedIds([])}
              />
            )}
          </div>
        )}
      </PageShell>

      <BulkApproveForm
        show={bulkOpen}
        ids={selectedIds}
        onHide={() => setBulkOpen(false)}
        onDone={() => { setSelectedIds([]); fetchList(); }}
      />

      <InvoiceFilesModal
        show={Boolean(filesFor)}
        paymentRequest={filesFor}
        onHide={() => setFilesFor(null)}
      />

      <PaymentRequestSettingsModal show={settingsOpen} onHide={() => setSettingsOpen(false)} isAdmin={isAdmin} />
    </>
  );
};

export default PaymentRequests;
